import React, { useEffect, useState } from "react";
import { Save, ToggleLeft, ToggleRight, Loader2, Receipt, AlertCircle, Info } from "lucide-react";
import { adminGetInvoiceSettings, adminUpdateInvoiceSettings } from "../../lib/api";

const FIELDS = [
  { key: "company_name", label: "Company name", placeholder: "BLACK HOLE STUDIO" },
  { key: "address", label: "Address", placeholder: "Street, City - PIN", textarea: true },
  { key: "phone", label: "Phone (optional)", placeholder: "+91…" },
  { key: "email", label: "Email shown on invoice", placeholder: "info@livemover.com" },
  { key: "gstin", label: "GSTIN (15 chars, optional)", placeholder: "27AAAAA0000A1Z5" },
  { key: "seller_state", label: "Seller state (India)", placeholder: "Gujarat" },
  { key: "invoice_prefix", label: "Invoice number prefix", placeholder: "LM-" },
  { key: "logo_url", label: "Logo URL (optional)", placeholder: "https://…/logo.png" },
  { key: "footer_note", label: "Invoice footer note", placeholder: "Thank you for choosing…", textarea: true },
];

export default function InvoiceSettingsPanel({ onToast }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetInvoiceSettings().then(setData).catch(() => onToast?.("Failed to load invoice settings", "error"));
  }, [onToast]);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const fresh = await adminUpdateInvoiceSettings(data);
      setData(fresh);
      onToast?.("Invoice settings saved");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Save failed", "error");
    }
    setSaving(false);
  };

  if (!data) {
    return (
      <div className="py-12 text-center text-[#11151C]/50">
        <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
      </div>
    );
  }

  const toggleGst = () => setData({ ...data, gst_enabled: !data.gst_enabled });

  return (
    <div data-testid="invoice-settings-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="lm-font font-extrabold text-[#11151C] text-2xl flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#FF6B3D]" /> Invoice Settings
          </h2>
          <p className="text-[#11151C]/60 text-sm mt-1 max-w-xl">
            These values appear on every paid invoice + automated SES receipt email. Changes apply to <strong>future</strong> orders only — existing invoices keep the snapshot they were generated with.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="invoice-settings-save"
          className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>

      {/* GST toggle */}
      <div className="border border-[#11151C]/10 rounded-2xl p-5 mb-5 bg-[#FFFBF6]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-bold text-[#11151C] inline-flex items-center gap-2">
              {data.gst_enabled ? <ToggleRight className="w-6 h-6 text-emerald-500" /> : <ToggleLeft className="w-6 h-6 text-[#11151C]/40" />}
              GST {data.gst_enabled ? "enabled" : "disabled"}
            </div>
            <p className="text-xs text-[#11151C]/60 mt-1 max-w-md">
              When enabled, GST is added on top of every plan price at checkout. Razorpay charges the GST-inclusive total.
              The branded invoice email shows a full GST breakdown.
            </p>
          </div>
          <button
            onClick={toggleGst}
            data-testid="invoice-settings-toggle-gst"
            className={`px-4 py-2 rounded-xl text-sm font-bold ${data.gst_enabled ? "bg-emerald-500 text-white" : "bg-white border border-[#11151C]/15 text-[#11151C]"}`}
          >
            {data.gst_enabled ? "Disable GST" : "Enable GST"}
          </button>
        </div>

        {data.gst_enabled && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/65">GST mode</span>
              <select
                value={data.gst_mode}
                onChange={(e) => setData({ ...data, gst_mode: e.target.value })}
                data-testid="invoice-settings-gst-mode"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm bg-white"
              >
                <option value="IGST">IGST (single line, inter-state)</option>
                <option value="CGST_SGST">CGST + SGST (split, intra-state)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/65">GST rate (%)</span>
              <input
                type="number"
                step="0.01"
                value={data.gst_rate}
                onChange={(e) => setData({ ...data, gst_rate: Number(e.target.value) })}
                data-testid="invoice-settings-gst-rate"
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
              />
            </label>
            <div className="flex items-end">
              <p className="text-xs text-[#11151C]/65 bg-white border border-[#11151C]/10 rounded-xl px-3 py-2 inline-flex items-center gap-2">
                <Info className="w-4 h-4 text-[#FF6B3D]" />
                On ₹1,000 plan: tax ₹{((data.gst_rate || 0) * 10).toFixed(2)} → total ₹{(1000 + (data.gst_rate || 0) * 10).toFixed(2)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Company fields */}
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <label key={f.key} className={`block ${f.textarea ? "sm:col-span-2" : ""}`}>
            <span className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/65">{f.label}</span>
            {f.textarea ? (
              <textarea
                value={data[f.key] || ""}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                rows={3}
                data-testid={`invoice-settings-${f.key}`}
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
              />
            ) : (
              <input
                value={data[f.key] || ""}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                data-testid={`invoice-settings-${f.key}`}
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm"
              />
            )}
          </label>
        ))}
      </div>

      {!data.gstin && data.gst_enabled && (
        <div className="mt-5 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          GST is enabled but no GSTIN is set — the tax invoice won't show your GSTIN. Add it above before going live.
        </div>
      )}
    </div>
  );
}
